// 가격/중량 정산 공통 계산.
// 정육 실중량 정산(PR-B)에서 서버가 권위 있게 최종 금액을 계산할 때 쓰는 순수 함수들.
//  - 금액은 모두 원 단위 정수. 최종 표시/정산은 10원 단위로 반올림한다(사용자 결정).
//  - 서버(정산 확정)와 관리자 미리보기가 반드시 같은 결과를 내야 하므로 공식은 여기 한 곳에만 둔다.

// 10원 단위 반올림. 0.5 경계는 위로(Math.round 기본).
export function roundKrwToTen(krw: number): number {
  return Math.round(krw / 10) * 10;
}

// 라인에 선택된 추가 옵션 1종(옵션 단가/중량 + 고른 개수).
// 상품 옵션 마스터(ProductOptionDto)와 주문 스냅샷 양쪽에서 이 형태로 넘긴다.
export interface LineOptionAmount {
  addGrams: number;
  addPriceKrw: number;
  quantity: number;
}

// 라인의 추가 옵션 합계 금액(원).
export function sumOptionPriceKrw(options: readonly LineOptionAmount[] | undefined): number {
  if (!options || options.length === 0) {
    return 0;
  }
  return options.reduce((sum, o) => sum + Math.max(0, o.addPriceKrw) * Math.max(0, o.quantity), 0);
}

// 라인의 추가 옵션 합계 중량(g).
export function sumOptionGrams(options: readonly LineOptionAmount[] | undefined): number {
  if (!options || options.length === 0) {
    return 0;
  }
  return options.reduce((sum, o) => sum + Math.max(0, o.addGrams) * Math.max(0, o.quantity), 0);
}

// 라인의 예상 금액(원) = 단가 * 수량 + 옵션 합계.
// 장바구니 표시, 주문 생성 시 line_total_krw, 실중량 정산의 기준 금액이 모두 이 값이다.
export function lineExpectedPriceKrw(
  unitPriceKrw: number,
  quantity: number,
  options?: readonly LineOptionAmount[],
): number {
  return unitPriceKrw * quantity + sumOptionPriceKrw(options);
}

// 라인의 예상 총중량(g) = 기준중량 * 수량 + 옵션 합계 중량.
// 기준중량을 모르면(null) 기본 파트는 0 으로 보고 옵션 중량만 센다.
export function lineExpectedGramsG(
  baseGramsG: number | null | undefined,
  quantity: number,
  options?: readonly LineOptionAmount[],
): number {
  const base = baseGramsG && baseGramsG > 0 ? baseGramsG * quantity : 0;
  return base + sumOptionGrams(options);
}

// 무게가변 항목의 실중량 기준 라인 합계(합산 정산).
//  - 관리자는 그 줄의 "실제 총 중량"(기본+추가옵션 포함)을 한 번 입력한다.
//  - 예상 금액을 (실중량 / 예상 총중량) 비율로 환산한 뒤 10원 단위 반올림한다.
//  - 옵션이 없으면 예상금액=단가*수량, 예상중량=기준중량*수량 이므로 (단가 * 실중량 / 기준중량) 과
//    수학적으로 동일하다. 즉 옵션 도입 전 주문의 계산 결과는 바뀌지 않는다.
//  - 예상 총중량이 0 이하면 환산 불가이므로 0 을 반환한다(호출부에서 예상치 유지 등으로 처리).
export function computeSettledLineTotalKrw(
  expectedPriceKrw: number,
  expectedGramsG: number | null | undefined,
  actualWeightG: number,
): number {
  if (!expectedGramsG || expectedGramsG <= 0 || actualWeightG < 0) {
    return 0;
  }
  return roundKrwToTen((expectedPriceKrw * actualWeightG) / expectedGramsG);
}
